/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { humanizeLearningTime } from 'js/utils/DateTimeUtils';

import { Typography2, VisuallyHidden, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import _t from 'i18n!nls/compound-assessments';

type Props = {
  headerText: string;
  itemTypeText: string;
  timeCommitment?: number;
  totalPoints?: number;
};

const styles = {
  headerText: (theme: Theme) =>
    css({
      [breakpoints.down('xs')]: {
        letterSpacing: '0.4px',
      },
    }),
};
export const HeaderLeft: React.FC<Props> = ({ headerText, itemTypeText, timeCommitment, totalPoints }) => {
  const BulletDivider = (
    <Fragment>
      <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
        .
      </VisuallyHidden>
      <span aria-hidden="true">&nbsp;•&nbsp;</span>
    </Fragment>
  );

  return (
    <div data-testid="HeaderLeft">
      <Typography2 variant="subtitleMedium" component="h1" css={styles.headerText} data-testid="HeaderLeft__header">
        {headerText}
      </Typography2>
      <Typography2 component="p" variant="bodySecondary" color="supportText" data-testid="HeaderLeft__sub-header">
        {itemTypeText}
        {typeof timeCommitment === 'number' && timeCommitment > 0 && (
          <span data-testid="time-commitment">
            {BulletDivider}
            {humanizeLearningTime(timeCommitment)}
          </span>
        )}
        {typeof totalPoints === 'number' && totalPoints > 0 && (
          <Fragment>
            {BulletDivider}
            {/* Setting an aria-label prop on span makes it announce as a "group" and using role="text" is not standardized */}
            <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
              <FormattedMessage
                message={_t('{totalPoints, plural, =1 {1 total point} other {{totalPoints} total points}} available.')}
                totalPoints={totalPoints}
              />
            </VisuallyHidden>
            <span data-testid="total-points" aria-hidden="true">
              <FormattedMessage
                message={_t('{totalPoints, plural, =1 {1 total point} other {{totalPoints} total points}}')}
                totalPoints={totalPoints}
              />
            </span>
          </Fragment>
        )}
      </Typography2>
    </div>
  );
};

export default HeaderLeft;
